import { Building2, ChevronLeft, ChevronRight, Smartphone, Tags, Video, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useCurrentUser, useOrganization, useUpdateOrgBranding } from "@/api/hooks";
import { PhoneInput } from "@/components/PhoneInput";
import { formatPhoneForDisplay } from "@/lib/phoneFormat";

/** Per-browser localStorage flag — once dismissed, the wizard
 *  doesn't re-render for this user on this device. They'll still
 *  get the on-dashboard checklist as the persistent reminder. */
const DISMISSED_KEY = "pencilout_welcome_dismissed";

interface BasicSlide {
  kind: "basic";
  title: string;
  body: string;
  icon: typeof Video;
}

interface ContactSlide {
  kind: "contact";
}

type Slide = BasicSlide | ContactSlide;

const SLIDES: Slide[] = [
  {
    kind: "basic",
    title: "Walk the job. Hit Share. Quote ready.",
    body:
      "PencilOut turns a phone-recorded site walkthrough into a " +
      "draft quote in 20-30 minutes. You talk through what you " +
      "see; the AI extracts scope, applies your prices, and " +
      "produces line items you can review + send.",
    icon: Video,
  },
  {
    kind: "basic",
    title: "Recording happens on your iPhone",
    body:
      "Install PencilOut on the phone you carry to job sites. " +
      "Record the walkthrough, hit Share → PencilOut, draft " +
      "lands when the AI finishes processing. The web app you're " +
      "looking at right now is for everything OTHER than " +
      "recording — quote review, pricing, invoices, replies.",
    icon: Smartphone,
  },
  {
    kind: "basic",
    title: "Set your pricing first",
    body:
      "Your prices drive every AI-generated quote. PencilOut seeds " +
      "starter rules; tune them once and reuse forever. Skip this " +
      "and the AI generates plausible numbers; do it now and the " +
      "drafts will reflect your actual market.",
    icon: Tags,
  },
  {
    kind: "contact",
  },
];

export function WelcomeWizard() {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(DISMISSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [index, setIndex] = useState(0);

  if (dismissed) return null;

  const slide = SLIDES[index];
  if (!slide) return null;
  const isLast = index === SLIDES.length - 1;
  const isFirst = index === 0;

  const onDismiss = () => {
    try {
      window.localStorage.setItem(DISMISSED_KEY, "true");
    } catch {
      /* localStorage unavailable */
    }
    setDismissed(true);
  };

  return (
    // biome-ignore lint/a11y/useSemanticElements: custom modal pattern; native <dialog> has different semantics (showModal/close lifecycle, top-layer rendering) that this component doesn't use.
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-wizard-title"
      className="fixed inset-0 z-[90] flex items-center justify-center bg-graphite/40 px-6 py-12"
      onClick={(e) => {
        // Click on backdrop dismisses; clicks inside don't bubble.
        if (e.target === e.currentTarget) onDismiss();
      }}
      onKeyDown={(e) => {
        // Esc dismisses (keyboard equivalent of backdrop click).
        if (e.key === "Escape") onDismiss();
      }}
    >
      <div className="w-full max-w-lg rounded-lg border border-rule bg-paper-warm shadow-lg">
        <div className="px-5 py-5">
          <div className="flex items-start justify-between">
            <span className="brand-wordmark text-xl">PencilOut</span>
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Skip for now"
              className="text-graphite-soft hover:text-graphite"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>

          <div className="mt-6">
            {slide.kind === "basic" ? (
              <BasicSlideContent slide={slide} />
            ) : (
              <ContactSlideContent onAdvance={onDismiss} />
            )}
          </div>

          <div className="mt-6 flex items-center justify-center gap-1.5">
            {SLIDES.map((s, i) => (
              <span
                key={s.kind === "basic" ? s.title : s.kind}
                className={[
                  "h-1.5 rounded-full transition-all",
                  i === index ? "w-6 bg-graphite" : "w-1.5 bg-rule",
                ].join(" ")}
              />
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-rule bg-paper px-5 py-3">
          <button
            type="button"
            onClick={() => setIndex(index - 1)}
            disabled={isFirst}
            className="rounded px-3 py-1.5 font-mono text-sm text-graphite-soft transition hover:text-graphite disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onDismiss}
              className="rounded px-3 py-1.5 font-mono text-sm text-graphite-soft transition hover:text-graphite"
            >
              Skip for now
            </button>
            {!isLast && (
              <button
                type="button"
                onClick={() => setIndex(index + 1)}
                className="rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft"
              >
                Next
                <ChevronRight className="ml-1 inline h-3.5 w-3.5" aria-hidden />
              </button>
            )}
            {/* Last slide (contact form) has its own primary action
                inside ContactSlideContent — Save & finish. We just
                render the Skip option here. */}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// Basic informational slides — text + icon, no form.
// ---------------------------------------------------------------

function BasicSlideContent({ slide }: { slide: BasicSlide }) {
  const Icon = slide.icon;
  return (
    <div className="flex items-start gap-4">
      <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-pencil-yellow-soft">
        <Icon className="h-5 w-5 text-graphite" aria-hidden />
      </div>
      <div className="flex-1">
        <h2
          id="welcome-wizard-title"
          className="font-mono text-base font-bold tracking-tight text-graphite"
        >
          {slide.title}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-graphite-soft">{slide.body}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// Contact slide — inline form for business phone + email + address.
//
// This is the actual UX-gap fix. Without these fields the quote PDF
// has no callback info for the homeowner; the server hard-blocks
// quote-send when phone or address is missing. The wizard's job is
// to collect them at first sign-in so the contractor never hits the
// hard gate later.
//
// The form pre-populates from the org row — re-running the wizard
// on a returning user's clean device shows their existing values
// and lets them tweak. Skip-for-now still works (server gate on
// /quotes/{id}/send is the safety net), but the wizard's primary
// "Save & finish" CTA encourages doing it here.
// ---------------------------------------------------------------

function ContactSlideContent({ onAdvance }: { onAdvance: () => void }) {
  const me = useCurrentUser();
  const orgId = me.data?.organization_id ?? null;
  const orgQuery = useOrganization(orgId ?? undefined);
  const update = useUpdateOrgBranding(orgId ?? "");

  const [phone, setPhone] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  // Hydrate from org once it loads.
  useEffect(() => {
    if (!orgQuery.data) return;
    setPhone(formatPhoneForDisplay(orgQuery.data.business_phone));
    setEmail(orgQuery.data.business_email ?? "");
    setAddress(orgQuery.data.business_address ?? "");
  }, [orgQuery.data]);

  async function handleSave(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!orgId) {
      setError("No organization context — sign in again.");
      return;
    }
    const trimmedPhone = phone.trim();
    const trimmedAddr = address.trim();
    if (!trimmedPhone || !trimmedAddr) {
      setError("Phone and address are required to send quotes.");
      return;
    }
    try {
      await update.mutateAsync({
        business_phone: trimmedPhone,
        business_email: email.trim() ? email.trim() : null,
        business_address: trimmedAddr,
      });
      onAdvance();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.detail
          : err instanceof Error
            ? err.message
            : "Could not save.",
      );
    }
  }

  return (
    <form onSubmit={handleSave} className="space-y-3">
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-pencil-yellow-soft">
          <Building2 className="h-5 w-5 text-graphite" aria-hidden />
        </div>
        <div className="flex-1">
          <h2
            id="welcome-wizard-title"
            className="font-mono text-base font-bold tracking-tight text-graphite"
          >
            How does your business appear on quotes?
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-graphite-soft">
            Your phone and address print on every quote PDF the homeowner receives. Without them
            PencilOut can't send — they have no way to call you back.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 pt-2 sm:grid-cols-2">
        {/* biome-ignore lint/a11y/noLabelWithoutControl: <PhoneInput> renders the input internally as a descendant of this <label>; the DOM association works at runtime even though biome can't see through the component boundary. */}
        <label className="block">
          <span className="label-mono">
            Phone <span className="text-red-pencil">*</span>
          </span>
          <PhoneInput
            value={phone}
            onChange={setPhone}
            placeholder="(704) 555-0100"
            required
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm focus:border-graphite focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="label-mono">Contact email (optional)</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="hello@yourcompany.com"
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm focus:border-graphite focus:outline-none"
          />
        </label>
        <label className="col-span-1 block sm:col-span-2">
          <span className="label-mono">
            Address <span className="text-red-pencil">*</span>
          </span>
          <textarea
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder={"123 Main Street\nCharlotte, NC 28202"}
            rows={2}
            required
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm focus:border-graphite focus:outline-none"
          />
        </label>
      </div>

      {error && <p className="text-xs text-red-pencil">{error}</p>}

      <div className="flex items-center justify-end gap-2 pt-1">
        <Link
          to="/settings"
          onClick={onAdvance}
          className="rounded border border-rule px-3 py-1.5 font-mono text-xs text-graphite-soft hover:border-graphite hover:text-graphite"
        >
          More fields →
        </Link>
        <button
          type="submit"
          disabled={update.isPending || !phone.trim() || !address.trim()}
          className="rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-40"
        >
          {update.isPending ? "Saving…" : "Save & finish"}
        </button>
      </div>
    </form>
  );
}
